/**
 * Product page earliest-delivery widget.
 * Calls Pickeasy slots API and displays earliest delivery date for the entered postcode.
 * Used by snippet earliest-delivery.liquid.
 */

(function () {
  // Use the same signed Shopify app proxy as Pickeasy's cart widget. The
  // provider's direct endpoint no longer accepts unauthenticated requests.
  const SLOTS_API = '/apps/pickeasy/slots';
  const REQUEST_TIMEOUT_MS = 15000;
  const STORAGE_KEY = 'earliest_delivery_postcode';
  const COUNTDOWN_VISIBLE_WINDOW_MS = 12 * 60 * 60 * 1000;

  async function fetchWithTimeout(url, options, readResponse) {
    const controller = new AbortController();
    const timeout = setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, Object.assign({}, options, { signal: controller.signal }));
      return await readResponse(response);
    } finally {
      clearTimeout(timeout);
    }
  }

  function getConfig(root) {
    return {
      shop: root.dataset.shop || '',
      sectionId: root.dataset.sectionId || '',
      productId: root.dataset.productId || '',
      productHandle: root.dataset.productHandle || '',
      productPriceCents: parseInt(root.dataset.productPriceCents || '0', 10) || 0,
      cartUrl: root.dataset.cartUrl || '/cart',
      placeholder: root.dataset.placeholder || 'Enter your postcode',
      buttonLabel: root.dataset.buttonLabel || 'Check',
      prefix: root.dataset.prefix || 'Earliest delivery',
      suffix: root.dataset.suffix || 'to [postcode]',
      successMessage: root.dataset.successMessage || '',
      errorMessage: root.dataset.errorMessage || "Delivery isn't available to this area.",
      regionalMessage: root.dataset.regionalMessage || 'Our courier will organise a delivery day that suits you, usually within a week.',
      cartFallbackMessage: root.dataset.cartFallbackMessage || 'Postcode checker is temporarily unavailable on this page. Please use the [cart_link].',
      countdownTemplate: root.dataset.countdownTemplate || '',
      cutoffTime: root.dataset.cutoffTime || '',
      timezone: root.dataset.timezone || 'Australia/Perth',
      showPrice: root.dataset.showPrice === 'true',
      metroPrice: parseFloat(root.dataset.metroPrice) || 0,
      regionalPrice: parseFloat(root.dataset.regionalPrice) || 0,
      deliveryDiscount: parseFloat(root.dataset.deliveryDiscount) || 0,
      fallbackPickupMessage: root.dataset.fallbackPickupMessage || 'Or pick up from our Myaree Warehouse',
      newsletterLink: root.dataset.newsletterLink || '#shopify-section-footer',
      isDiscountable: root.dataset.isDiscountable === 'true',
    };
  }

  function getEarliestDateIso(dateValue) {
    var time = parseSlotDate(dateValue);
    if (isNaN(time)) return '';
    return new Date(time).toISOString().slice(0, 10);
  }

  function buildAnalyticsPayload(root, config, action, details) {
    var payload = details && typeof details === 'object' ? Object.assign({}, details) : {};
    var postcode = normalisePostcode(payload.postcode || '');
    delete payload.postcode;

    payload.checker_action = action;
    payload.widget = 'earliest_delivery';
    payload.section_id = config.sectionId || '';
    payload.product_id = config.productId || '';
    payload.product_handle = config.productHandle || '';
    payload.shop_domain = config.shop || '';
    payload.page_path = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
    payload.page_url = typeof window !== 'undefined' && window.location ? window.location.href : '';
    payload.postcode_length = postcode ? postcode.length : 0;
    payload.postcode_prefix = postcode ? postcode.slice(0, 2) : '';
    payload.timestamp = new Date().toISOString();

    if (payload.earliest_date && !payload.earliest_date_iso) {
      payload.earliest_date_iso = getEarliestDateIso(payload.earliest_date);
    }

    if (!payload.result_type) {
      payload.result_type = '';
    }

    return payload;
  }

  function emitPostcodeCheckerEvent(root, config, action, details) {
    var payload = buildAnalyticsPayload(root, config, action, details);

    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('postcode_checker', { detail: payload }));
        window.dispatchEvent(new CustomEvent('postcode_checker:' + action, { detail: payload }));
      }
    } catch (e) {}

    try {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: 'postcode_checker' }, payload));
      }
    } catch (e) {}

    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'postcode_checker', payload);
      }
    } catch (e) {}

    try {
      if (typeof window !== 'undefined' && window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('postcode_checker', payload);
      }
    } catch (e) {}

    root._lastPostcodeCheckerEvent = payload;
  }

  function normalisePostcode(value) {
    return String(value).trim().replace(/\s/g, '');
  }

  function getSlotDateValue(slot) {
    if (!slot) return '';
    if (typeof slot === 'string' || typeof slot === 'number') return slot;
    if (typeof slot !== 'object') return '';
    return slot.date || slot.deliveryDate || slot.delivery_date || slot.slotDate || slot.slot_date || slot.startDate || slot.start_date || slot.start || slot.slotStart || '';
  }

  function normaliseEpochMs(value) {
    var num = parseInt(value, 10);
    if (isNaN(num)) return NaN;
    // If seconds precision is provided, convert to milliseconds.
    if (num > 0 && num < 1000000000000) return num * 1000;
    return num;
  }

  function parseSlotDate(dateValue) {
    if (dateValue == null) return NaN;
    if (typeof dateValue === 'number') return normaliseEpochMs(dateValue);
    if (dateValue instanceof Date) return dateValue.getTime();

    var raw = String(dateValue).trim();
    if (!raw) return NaN;
    if (/^(mon|tue|wed|thu|fri|sat|sun)(day)?$/i.test(raw)) return NaN;

    var epochMatch = raw.match(/\/?Date\((\d{10,})\)\/?/i);
    if (epochMatch) return normaliseEpochMs(epochMatch[1]);

    var dmyMatch = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})(?:\D|$)/);
    if (dmyMatch) {
      var dmyDate = new Date(parseInt(dmyMatch[3], 10), parseInt(dmyMatch[2], 10) - 1, parseInt(dmyMatch[1], 10), 12, 0, 0, 0);
      return dmyDate.getTime();
    }

    var ymdMatch = raw.match(/^(\d{4})[\/.-](\d{1,2})[\/.-](\d{1,2})(?:\D|$)/);
    if (ymdMatch) {
      var ymdDate = new Date(parseInt(ymdMatch[1], 10), parseInt(ymdMatch[2], 10) - 1, parseInt(ymdMatch[3], 10), 12, 0, 0, 0);
      return ymdDate.getTime();
    }

    var parsed = Date.parse(raw);
    return isNaN(parsed) ? NaN : parsed;
  }

  function isTrueValue(value) {
    return value === true || String(value).toLowerCase() === 'true';
  }

  function isFalseValue(value) {
    return value === false || String(value).toLowerCase() === 'false';
  }

  function isSlotUnavailable(slot) {
    if (!slot || typeof slot !== 'object') return false;

    if (isTrueValue(slot.disabled) || isTrueValue(slot.isDisabled) || isTrueValue(slot.blocked) || isTrueValue(slot.isBlocked) || isTrueValue(slot.closed) || isTrueValue(slot.isClosed)) {
      return true;
    }

    if (isFalseValue(slot.available) || isFalseValue(slot.isAvailable)) {
      return true;
    }

    if (!Array.isArray(slot.slots) || slot.slots.length === 0) {
      return false;
    }

    var hasAvailableWindow = slot.slots.some(function (windowSlot) {
      if (!windowSlot || typeof windowSlot !== 'object') return true;
      if (isTrueValue(windowSlot.disabled) || isTrueValue(windowSlot.isDisabled) || isTrueValue(windowSlot.blocked) || isTrueValue(windowSlot.isBlocked) || isTrueValue(windowSlot.closed) || isTrueValue(windowSlot.isClosed)) {
        return false;
      }
      if (isFalseValue(windowSlot.available) || isFalseValue(windowSlot.isAvailable)) {
        return false;
      }
      return true;
    });

    return !hasAvailableWindow;
  }

  function getEarliestSlot(slots) {
    if (!Array.isArray(slots) || slots.length === 0) return null;

    var parsedSlots = slots.map(function (slot) {
      return {
        slot: slot,
        time: parseSlotDate(getSlotDateValue(slot)),
        unavailable: isSlotUnavailable(slot),
      };
    }).filter(function (candidate) {
      return !isNaN(candidate.time);
    });

    if (parsedSlots.length === 0) {
      return null;
    }

    var availableSlots = parsedSlots.filter(function (candidate) {
      return !candidate.unavailable;
    });
    var candidates = availableSlots.length > 0 ? availableSlots : parsedSlots;

    candidates.sort(function (a, b) {
      return a.time - b.time;
    });

    return candidates[0].slot;
  }

  function formatDateForDisplay(dateValue) {
    if (!dateValue) return '';
    var time = parseSlotDate(dateValue);
    if (isNaN(time)) return String(dateValue);
    const d = new Date(time);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) return 'Today';
    if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return d.toLocaleDateString(undefined, options);
  }

  function buildDateMarkup(dateDisplay) {
    var safeDate = escapeHtml(dateDisplay || '');
    return '<strong class="earliest-delivery__date">' + safeDate + '</strong>';
  }

  function showResult(el, html, isError) {
    if (!el) return;
    el.hidden = false;
    el.innerHTML = html;
    el.classList.toggle('earliest-delivery__result--error', !!isError);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function buildCountdownHtml(template, countdownStr, dayLabel) {
    var token = '__EARLIEST_DELIVERY_COUNTDOWN__';
    var safeTemplate = escapeHtml(template || '');
    var safeCountdown = escapeHtml(countdownStr || '');
    var safeDay = escapeHtml(dayLabel || '');
    var html = safeTemplate
      .replace(/\[countdown\]/gi, token)
      .replace(/\[day\]/gi, safeDay)
      .replace(new RegExp(token, 'g'), '<strong class="earliest-delivery__countdown-time" style="padding: 0 0.2rem">' + safeCountdown + '</strong>');

    if (!/^\s*🕒/.test(html)) {
      html = '🕒 <span style="margin-left:0.2rem"></span>' + html.trim();
    }

    return html;
  }

  function formatCountdown(ms) {
    if (ms <= 0) return '0m';
    var totalMins = Math.floor(ms / 60000);
    var hours = Math.floor(totalMins / 60);
    var mins = totalMins % 60;
    if (hours > 0) return hours + 'h ' + mins + 'm';
    return mins + 'm';
  }

  function getNextCutoffInTz(cutoffTime, timezone, fromDate) {
    var parts = String(cutoffTime).trim().split(/[:\s]/);
    var hour = parseInt(parts[0], 10) || 14;
    var minute = parts[1] ? parseInt(parts[1], 10) : 0;
    var date = fromDate ? new Date(fromDate) : new Date();
    date.setHours(hour, minute, 0, 0);
    return date.getTime();
  }

  function startCountdown(resultEl, countdownEl, config, deliveryDateStr, postcode) {
    if (!config.countdownTemplate || !config.cutoffTime) return;
    var deliveryDateMs = parseSlotDate(deliveryDateStr);
    if (isNaN(deliveryDateMs)) return;
    var deliveryDate = new Date(deliveryDateMs);
    if (isNaN(deliveryDate.getTime())) return;
    var dayBefore = new Date(deliveryDate);
    dayBefore.setDate(dayBefore.getDate() - 1);
    var cutoffMs = getNextCutoffInTz(config.cutoffTime, config.timezone, dayBefore);
    var now = Date.now();
    if (cutoffMs <= now) {
      dayBefore.setDate(dayBefore.getDate() + 2);
      cutoffMs = getNextCutoffInTz(config.cutoffTime, config.timezone, dayBefore);
      deliveryDate = new Date(dayBefore);
      deliveryDate.setDate(deliveryDate.getDate() + 1);
      deliveryDateStr = deliveryDate.toISOString();
    }
    var dayLabel = formatDateForDisplay(deliveryDateStr);

    function tick() {
      now = Date.now();
      if (now >= cutoffMs) {
        dayBefore.setDate(dayBefore.getDate() + 2);
        cutoffMs = getNextCutoffInTz(config.cutoffTime, config.timezone, dayBefore);
        deliveryDate = new Date(dayBefore);
        deliveryDate.setDate(deliveryDate.getDate() + 1);
        dayLabel = formatDateForDisplay(deliveryDate.toISOString());
      }
      if (countdownEl) {
        var remainingMs = cutoffMs - now;
        if (remainingMs > COUNTDOWN_VISIBLE_WINDOW_MS) {
          countdownEl.hidden = true;
          countdownEl.innerHTML = '';
          return;
        }
        var countdownStr = formatCountdown(remainingMs);
        var html = buildCountdownHtml(config.countdownTemplate, countdownStr, dayLabel);
        countdownEl.innerHTML = html;
        countdownEl.hidden = false;
      }
    }
    tick();
    var interval = setInterval(tick, 60000);
    if (countdownEl._countdownInterval) clearInterval(countdownEl._countdownInterval);
    countdownEl._countdownInterval = interval;
  }

  function setLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function resetCountdown(countdownEl) {
    if (!countdownEl) return;
    if (countdownEl._countdownInterval) {
      clearInterval(countdownEl._countdownInterval);
      countdownEl._countdownInterval = null;
    }
    countdownEl.hidden = true;
  }

  function syncPostcodeTriggerState(formEl) {
    if (!formEl) return;
    var root = formEl.closest('[data-earliest-delivery]');
    if (!root) return;
    var isOpen = !formEl.hidden;
    root.classList.toggle('is-editing', isOpen);
    var triggers = root.querySelectorAll('.earliest-delivery__postcode-trigger');
    triggers.forEach(function (btn) {
      btn.classList.toggle('is-open', isOpen);
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function setFormVisible(formEl, visible) {
    if (!formEl) return;
    formEl.hidden = !visible;
    syncPostcodeTriggerState(formEl);
  }

  function buildCartCheckerLink(config) {
    var cartUrl = escapeHtml(config && config.cartUrl ? config.cartUrl : '/cart');
    return '<a class="earliest-delivery__cart-link" href="' + cartUrl + '">postcode checker in cart</a>';
  }

  function buildCartFallbackHtml(config) {
    var template = (config && config.cartFallbackMessage) || 'Postcode checker is temporarily unavailable on this page. Please use the [cart_link].';
    var token = '__EARLIEST_DELIVERY_CART_LINK__';
    var safeTemplate = escapeHtml(template);
    var hasToken = /\[cart_link\]/i.test(template);
    var linkHtml = buildCartCheckerLink(config);
    var html = safeTemplate
      .replace(/\[cart_link\]/gi, token)
      .replace(new RegExp(token, 'g'), linkHtml);

    if (!hasToken) {
      html = (html ? html + ' ' : '') + 'Please use the ' + linkHtml + '.';
    }

    return html;
  }

  function renderCartFallback(root, resultEl, formEl, countdownEl, config) {
    resetCountdown(countdownEl);
    showResult(resultEl, buildCartFallbackHtml(config), true);
    setFormVisible(formEl, true);
    root._regionalDelivery = false;
    root._lastDeliveryDate = '';
  }

  function renderMatchedLocationNoSlots(root, resultEl, formEl, countdownEl, input, config, postcode) {
    var postcodeButton = buildPostcodeButton(postcode);
    var cartLink = buildCartCheckerLink(config);
    var line = 'Delivery available to ' + postcodeButton + '. Please choose your delivery date using the ' + cartLink + '.';
    showResult(resultEl, line, false);
    showPrice(root, false, config);
    setFormVisible(formEl, false);
    resetCountdown(countdownEl);
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
    } catch (e) {}
    root._lastDeliveryDate = '';
    root._lastPostcode = postcode;
    root._regionalDelivery = false;
    attachEditListener(root, formEl, input, postcode, countdownEl, resultEl, config);
  }

  function revealPostcodeForm(formEl, input, postcode, countdownEl) {
    setFormVisible(formEl, true);
    resetCountdown(countdownEl);
    if (!input) return;
    if (typeof postcode === 'string') input.value = postcode;
    input.focus();
    input.select();
  }

  function resolveMonthYear(monthOffset) {
    var offset = typeof monthOffset === 'number' ? monthOffset : 0;
    var reference = new Date();
    return new Date(reference.getFullYear(), reference.getMonth() + offset, 1);
  }

  function hasSlots(data) {
    return !!(data && Array.isArray(data.slots) && data.slots.length > 0);
  }

  /**
   * Build request body for slots API.
   * Keep payload shape aligned with Pickeasy cart widget.
   */
  function buildPayload(shop, postcode, cart, locationId, productPriceCents, monthOffset) {
    var monthYear = resolveMonthYear(monthOffset);
    var cartOrderValue = 0;
    var cartCurrencyCode = 'AUD';
    var cartAttributes = {};
    if (cart && typeof cart === 'object') {
      cartOrderValue = typeof cart.total_price === 'number' ? cart.total_price / 100 : (parseInt(cart.total_price, 10) || 0) / 100;
      cartCurrencyCode = cart.currency || 'AUD';
      if (cart.attributes && typeof cart.attributes === 'object') {
        cartAttributes = cart.attributes;
      }
    }
    if (typeof productPriceCents === 'number' && productPriceCents > 0) {
      var productOrderValue = productPriceCents / 100;
      if (productOrderValue > cartOrderValue) {
        cartOrderValue = productOrderValue;
      }
    }

    var themeName = '';
    var themeRole = '';
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.theme) {
      themeName = window.Shopify.theme.name || '';
      themeRole = window.Shopify.theme.role || '';
    }

    var payload = {
      myShopifyDomain: shop,
      cartItems: [],
      cartOrderValue: cartOrderValue,
      cartCurrencyCode: cartCurrencyCode,
      isShopifyPOS: false,
      enableInstoreAppIntegration: false,
      fulfillmentType: 'delivery',
      currentPageURL: typeof window !== 'undefined' && window.location ? window.location.href : '',
      cartAttributes: cartAttributes,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      // Pickeasy expects a zero-based month index (Jan = 0, Feb = 1).
      month: monthYear.getMonth(),
      year: monthYear.getFullYear(),
      theme: { name: themeName, role: themeRole },
    };
    if (postcode) {
      payload.zip = postcode;
    }
    if (locationId) {
      payload.locationId = locationId;
    }
    return payload;
  }

  function parsePostcodeNumber(postcode) {
    var digits = String(postcode || '').replace(/\D/g, '');
    if (!digits) return null;
    var parsed = parseInt(digits, 10);
    return isNaN(parsed) ? null : parsed;
  }

  function postcodeMatchesToken(token, postcodeStr, postcodeNum) {
    var value = String(token || '').trim();
    if (!value) return false;

    if (/^\d+$/.test(value)) {
      return value === postcodeStr;
    }

    var wildcardMatch = value.match(/^(\d+)\*$/);
    if (wildcardMatch) {
      return postcodeStr.indexOf(wildcardMatch[1]) === 0;
    }

    var rangeMatch = value.match(/^(\d+)\s*[-–—]\s*(\d+)$/) || value.match(/^(\d+)\s*to\s*(\d+)$/i);
    if (rangeMatch && postcodeNum != null) {
      var start = parseInt(rangeMatch[1], 10);
      var end = parseInt(rangeMatch[2], 10);
      if (isNaN(start) || isNaN(end)) return false;
      if (start > end) {
        var swap = start;
        start = end;
        end = swap;
      }
      return postcodeNum >= start && postcodeNum <= end;
    }

    return false;
  }

  function postcodeMatchesZipcodes(rawZipcodes, postcodeStr, postcodeNum) {
    if (rawZipcodes == null) return false;

    if (Array.isArray(rawZipcodes)) {
      return rawZipcodes.some(function (entry) {
        return postcodeMatchesZipcodes(entry, postcodeStr, postcodeNum);
      });
    }

    if (typeof rawZipcodes === 'object') {
      var exactCode = rawZipcodes.zip || rawZipcodes.postcode || rawZipcodes.code;
      if (exactCode != null && normalisePostcode(exactCode) === postcodeStr) {
        return true;
      }

      var rangeStart = rawZipcodes.startZip || rawZipcodes.start || rawZipcodes.from || rawZipcodes.min;
      var rangeEnd = rawZipcodes.endZip || rawZipcodes.end || rawZipcodes.to || rawZipcodes.max;
      if (rangeStart != null && rangeEnd != null && postcodeNum != null) {
        var startNum = parsePostcodeNumber(rangeStart);
        var endNum = parsePostcodeNumber(rangeEnd);
        if (startNum != null && endNum != null) {
          if (startNum > endNum) {
            var swapNum = startNum;
            startNum = endNum;
            endNum = swapNum;
          }
          return postcodeNum >= startNum && postcodeNum <= endNum;
        }
      }

      return false;
    }

    var groups = String(rawZipcodes).split(/[,\n;]+/);
    for (var i = 0; i < groups.length; i++) {
      var group = groups[i].trim();
      if (!group) continue;

      var looksLikeRange = /[-–—]/.test(group) || /\bto\b/i.test(group);
      var tokens = looksLikeRange ? [group] : group.split(/\s+/);
      for (var j = 0; j < tokens.length; j++) {
        if (postcodeMatchesToken(tokens[j], postcodeStr, postcodeNum)) {
          return true;
        }
      }
    }

    return false;
  }

  function findDeliveryLocationIdForPostcode(setup, postcode) {
    if (!setup || !Array.isArray(setup.locations) || !postcode) return null;
    var postcodeStr = normalisePostcode(postcode);
    var postcodeNum = parsePostcodeNumber(postcodeStr);

    for (var i = 0; i < setup.locations.length; i++) {
      var loc = setup.locations[i];
      if (!loc || !loc.id) continue;
      var isDelivery = loc.type === 'delivery' || loc.fulfillmentType === 'delivery';
      if (!isDelivery) continue;
      if (postcodeMatchesZipcodes(loc.zipcodes, postcodeStr, postcodeNum)) {
        return loc.id;
      }
    }

    return null;
  }

  function isNoDateDeliveryLocation(setup, locationId) {
    if (!setup || !locationId) return false;
    var list = setup.noDateAndTimeDeliveryLocation;
    if (!Array.isArray(list)) return false;
    return list.map(String).indexOf(String(locationId)) !== -1;
  }

  function buildPostcodeButton(postcode, options) {
    var safePostcode = escapeHtml(postcode);
    var displayText = options && options.short ? safePostcode : 'Postcode ' + safePostcode;
    return '<button type="button" class="earliest-delivery__postcode-trigger" data-earliest-delivery-edit="" aria-label="Change delivery postcode ' + safePostcode + '">' + displayText + '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="earliest-delivery__chevron" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg></button>';
  }

  function attachPostcodeToggleListener(root, selector, formEl, input, postcode, countdownEl, resultEl, config, analyticsSource) {
    var toggleBtn = root.querySelector(selector);
    if (!toggleBtn) return;
    if (toggleBtn.dataset.toggleBound === 'true') return;
    toggleBtn.dataset.toggleBound = 'true';
    toggleBtn.addEventListener('click', function () {
      var isOpen = !!(formEl && !formEl.hidden);
      if (isOpen) {
        setFormVisible(formEl, false);
        emitPostcodeCheckerEvent(root, config, 'form_closed', {
          source: analyticsSource,
          postcode: postcode || '',
          result_type: 'close',
        });
        return;
      }
      revealPostcodeForm(formEl, input, postcode, countdownEl);
      if (resultEl) resultEl.hidden = false;
      emitPostcodeCheckerEvent(root, config, 'form_opened', {
        source: analyticsSource,
        postcode: postcode || '',
        result_type: analyticsSource === 'initial_prompt' ? 'open' : 'edit',
      });
    });
  }

  function attachEditListener(root, formEl, input, postcode, countdownEl, resultEl, config) {
    attachPostcodeToggleListener(root, '[data-earliest-delivery-edit]', formEl, input, postcode, countdownEl, resultEl, config, 'postcode_link');
  }

  function buildPriceMessage(isRegional, config) {
    if (!config.showPrice) return '';

    var basePrice = isRegional ? config.regionalPrice : config.metroPrice;
    var discount = config.deliveryDiscount;
    
    // If a discount offer exists but this product is not eligible for it
    if (discount > 0 && !config.isDiscountable) {
      return config.fallbackPickupMessage || 'Or pick up from our Myaree Warehouse.';
    }

    var canApplyDiscount = !!(config.isDiscountable && discount > 0 && basePrice > 0);
    var finalPrice = canApplyDiscount ? Math.max(0, basePrice - discount) : basePrice;

    var finalPriceHtml = finalPrice === 0 ? '<strong>FREE</strong>' : '<strong>$' + finalPrice + '</strong>';
    var isModalTarget = config.newsletterLink === '#NewsletterModal';
    var linkHref = isModalTarget ? '#NewsletterModal' : escapeHtml(config.newsletterLink);
    var ariaAttr = isModalTarget ? ' aria-controls="NewsletterModal" aria-expanded="false"' : '';
    var modalDataAttr = isModalTarget ? ' data-newsletter-modal-link="true"' : '';
    var linkHtml = '<a href="' + linkHref + '" class="earliest-delivery__newsletter-link"' + ariaAttr + modalDataAttr + '>join the newsletter.</a>';
    var leadHtml = 'Delivered for ' + finalPriceHtml;

    if (canApplyDiscount) {
      leadHtml += ' <span class="earliest-delivery__compare-price">$' + basePrice + '</span>';
      leadHtml = '<span class="earliest-delivery__price-card">' + leadHtml + '</span>';
    }

    var message = leadHtml;
    
    if (config.newsletterLink && canApplyDiscount) {
      message += ' when you ' + linkHtml;
    } else {
      message += '.';
    }
    return message;
  }

  function showPrice(root, isRegional, config) {
    var priceEl = root.querySelector('.earliest-delivery__price');
    if (!priceEl) return;
    
    var msg = buildPriceMessage(isRegional, config);
    if (msg) {
      priceEl.innerHTML = msg;
      priceEl.hidden = false;
    } else {
      priceEl.hidden = true;
      priceEl.innerHTML = '';
    }
  }

  function renderRegionalAvailability(root, resultEl, formEl, countdownEl, input, config, postcode) {
    var postcodeButton = buildPostcodeButton(postcode);
    var regionalTemplate = config.regionalMessage || '';
    var line = regionalTemplate.indexOf('[postcode]') !== -1
      ? regionalTemplate.replace(/\[postcode\]/gi, postcodeButton)
      : (regionalTemplate + ' ' + postcodeButton).trim();
    showResult(resultEl, line, false);
    showPrice(root, true, config);
    setFormVisible(formEl, false);
    resetCountdown(countdownEl);
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
    } catch (e) {}
    root._lastDeliveryDate = '';
    root._lastPostcode = postcode;
    root._regionalDelivery = true;
    attachEditListener(root, formEl, input, postcode, countdownEl, resultEl, config);
  }

  function runCheck(root, source) {
    source = source || 'manual';
    const input = root.querySelector('.earliest-delivery__input');
    const button = root.querySelector('[data-earliest-delivery-check]');
    const resultEl = root.querySelector('.earliest-delivery__result');
    const formEl = root.querySelector('.earliest-delivery__form');
    const countdownEl = root.querySelector('.earliest-delivery__countdown');
    const config = getConfig(root);
    const shouldTrack = source !== 'stored';

    if (!input || !resultEl) return;

    const postcode = normalisePostcode(input.value);
    if (!postcode) {
      showResult(resultEl, config.errorMessage, true);
      setFormVisible(formEl, true);
      if (shouldTrack) {
        emitPostcodeCheckerEvent(root, config, 'validation_failed', {
          source: source,
          result_type: 'validation_error',
          error_code: 'empty_postcode',
        });
      }
      return;
    }

    if (postcode === '0000') {
      renderCartFallback(root, resultEl, formEl, countdownEl, config);
      if (shouldTrack) {
        emitPostcodeCheckerEvent(root, config, 'check_failed', {
          source: source,
          postcode: postcode,
          result_type: 'technical_error',
          error_code: 'manual_test_postcode_0000',
          error_message: 'Manual fallback test via postcode 0000',
        });
      }
      return;
    }

    if (shouldTrack) {
      emitPostcodeCheckerEvent(root, config, 'check_started', {
        source: source,
        postcode: postcode,
        result_type: 'started',
      });
    }

    if (button) setLoading(button, true);
    showResult(resultEl, 'Checking…', false);
    resetCountdown(countdownEl);
    showPrice(root, false, { showPrice: false });

    function doRequest(cart, locationId, boostCartOrderValue, monthOffset) {
      const payload = buildPayload(config.shop, postcode, cart, locationId, config.productPriceCents, monthOffset);
      if (boostCartOrderValue) {
        payload.cartOrderValue = Math.max(payload.cartOrderValue || 0, 10000);
      }
      root._lastSlotsRequestPayload = Object.assign({}, payload, typeof monthOffset === 'number' ? { _monthOffset: monthOffset } : {});
      return fetchWithTimeout(SLOTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }, function (res) {
          return res.text().then(function (text) {
            if (!res.ok) {
              var responseMessage = 'Slots API request failed';
              if (text) {
                try {
                  var errorData = JSON.parse(text);
                  responseMessage = errorData.message || responseMessage;
                } catch (e) {}
              }
              var requestError = new Error(responseMessage + ' (HTTP ' + res.status + ')');
              requestError.status = res.status;
              throw requestError;
            }

            if (!text || !text.trim()) {
              throw new Error('Slots API returned an empty response');
            }

            try {
              var data = JSON.parse(text || '{}');
              if (!data || !Array.isArray(data.slots)) {
                throw new Error('Slots API response did not include a valid slots array');
              }
              root._lastSlotsResponse = data;
              return data;
            } catch (e) {
              if (e instanceof Error && e.message) throw e;
              throw new Error('Unable to parse slots response');
            }
          });
        });
    }

    function requestAcrossMonths(cart, locationId, boostCartOrderValue, monthOffsets) {
      var offsets = Array.isArray(monthOffsets) && monthOffsets.length ? monthOffsets : [0];
      var index = 0;
      var lastData = null;

      function runNext() {
        if (index >= offsets.length) {
          return Promise.resolve(lastData);
        }

        var offset = offsets[index++];
        return doRequest(cart, locationId, boostCartOrderValue, offset).then(function (data) {
          if (!data || typeof data !== 'object') {
            data = { slots: [] };
          }
          data._monthOffset = offset;
          if (lastData && lastData.setup && !data.setup) {
            data.setup = lastData.setup;
          }
          lastData = data;
          if (hasSlots(data)) {
            return data;
          }
          return runNext();
        });
      }

      return runNext();
    }

    var fetchCart = fetchWithTimeout('/cart.js', {}, function (r) { return r.json(); }).catch(function () { return null; });
    fetchCart.then(function (cart) {
      return doRequest(cart, null, false, 0).then(function (data) {
        var matchedLocationId = findDeliveryLocationIdForPostcode(data.setup, postcode);
        data._matchedLocationId = matchedLocationId || '';
        if (isNoDateDeliveryLocation(data.setup, matchedLocationId)) {
          data._regionalDelivery = true;
          data._regionalLocationId = matchedLocationId;
          return data;
        }

        if (hasSlots(data)) return data;
        if (!matchedLocationId) return data;

        return requestAcrossMonths(cart, matchedLocationId, false, [0, 1, 2]).then(function (retryData) {
          var setupForCheck = retryData.setup || data.setup;
          retryData._matchedLocationId = matchedLocationId || '';
          if (isNoDateDeliveryLocation(setupForCheck, matchedLocationId)) {
            retryData._regionalDelivery = true;
            retryData._regionalLocationId = matchedLocationId;
          }
          if (hasSlots(retryData)) {
            return retryData;
          }
          return requestAcrossMonths(cart, matchedLocationId, true, [0, 1, 2]).then(function (boostedData) {
            var boostedSetup = boostedData.setup || setupForCheck;
            boostedData._matchedLocationId = matchedLocationId || '';
            if (isNoDateDeliveryLocation(boostedSetup, matchedLocationId)) {
              boostedData._regionalDelivery = true;
              boostedData._regionalLocationId = matchedLocationId;
            }
            boostedData._boostedRequest = true;
            return boostedData;
          }).catch(function () {
            return retryData;
          });
        });
      });
    })
      .then(function (data) {
        if (data && data._regionalDelivery) {
          renderRegionalAvailability(root, resultEl, formEl, countdownEl, input, config, postcode);
          if (shouldTrack) {
            emitPostcodeCheckerEvent(root, config, 'check_success', {
              source: source,
              postcode: postcode,
              result_type: 'regional_no_date',
              regional: true,
              location_id: data._regionalLocationId || '',
            });
          }
          return;
        }

        const slots = data.slots;
        const earliest = getEarliestSlot(slots);
        const earliestDate = getSlotDateValue(earliest);
        if (earliest && earliestDate) {
          const dateDisplay = formatDateForDisplay(earliestDate);
          const dateMarkup = buildDateMarkup(dateDisplay);
          const suffixText = config.suffix.replace(/\[postcode\]/gi, postcode);
          // The dated line already reads "to [postcode]", so the button only needs the number.
          var postcodeButton = buildPostcodeButton(postcode, { short: true });
          var suffixWithButton = suffixText.replace(postcode, postcodeButton);
          var line = config.prefix + ' ' + dateMarkup + ' ' + suffixWithButton;
          if (config.successMessage) line = config.successMessage + ' ' + line;
          showResult(resultEl, line, false);
          showPrice(root, false, config);
          setFormVisible(formEl, false);
          try {
            localStorage.setItem(STORAGE_KEY, postcode);
          } catch (e) {}
          root._lastDeliveryDate = earliestDate;
          root._lastPostcode = postcode;
          root._regionalDelivery = false;
          if (config.countdownTemplate && config.cutoffTime && countdownEl) {
            startCountdown(resultEl, countdownEl, config, earliestDate, postcode);
          } else {
            resetCountdown(countdownEl);
          }
          attachEditListener(root, formEl, input, postcode, countdownEl, resultEl, config);
          if (shouldTrack) {
            emitPostcodeCheckerEvent(root, config, 'check_success', {
              source: source,
              postcode: postcode,
              result_type: 'metro_dated_slot',
              regional: false,
              location_id: earliest && earliest.locationId ? earliest.locationId : '',
              earliest_date: earliestDate,
            });
          }
        } else {
          var matchedLocationId = data && data._matchedLocationId ? String(data._matchedLocationId) : '';
          if (matchedLocationId) {
            renderMatchedLocationNoSlots(root, resultEl, formEl, countdownEl, input, config, postcode);
            if (shouldTrack) {
              emitPostcodeCheckerEvent(root, config, 'check_success', {
                source: source,
                postcode: postcode,
                result_type: 'matched_location_no_slots',
                location_id: matchedLocationId,
                regional: false,
                boosted_request: data && data._boostedRequest ? true : false,
              });
            }
          } else {
            resetCountdown(countdownEl);
            showResult(resultEl, config.errorMessage, true);
            setFormVisible(formEl, true);
            if (shouldTrack) {
              emitPostcodeCheckerEvent(root, config, 'check_unavailable', {
                source: source,
                postcode: postcode,
                result_type: 'no_delivery',
              });
            }
          }
        }
      })
      .catch(function (error) {
        console.error('[earliest-delivery] Slots lookup failed', error);
        renderCartFallback(root, resultEl, formEl, countdownEl, config);
        // Saved-postcode checks are customer-visible too. Always report their
        // technical failures, while keeping automatic successes out of analytics.
        emitPostcodeCheckerEvent(root, config, 'check_failed', {
          source: source,
          postcode: postcode,
          result_type: 'technical_error',
          error_code: error && error.name === 'AbortError' ? 'slots_request_timeout' : 'slots_request_failed',
          error_message: error && error.message ? error.message : '',
          http_status: error && error.status ? error.status : 0,
        });
      })
      .finally(function () {
        if (button) setLoading(button, false);
      });
  }

  function init() {
    const roots = document.querySelectorAll('[data-earliest-delivery]');
    roots.forEach(function (root) {
      const button = root.querySelector('[data-earliest-delivery-check]');
      const input = root.querySelector('.earliest-delivery__input');
      const resultEl = root.querySelector('.earliest-delivery__result');
      const formEl = root.querySelector('.earliest-delivery__form');
      const countdownEl = root.querySelector('.earliest-delivery__countdown');
      if (!input || !resultEl || !button) return;

      button.addEventListener('click', function () {
        runCheck(root, 'button');
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          runCheck(root, 'enter');
        }
      });

      root.addEventListener('click', function (e) {
        var trigger = e.target.closest('[aria-controls="NewsletterModal"]');
        if (!trigger) return;
        var modal = document.getElementById('NewsletterModal');
        if (modal && typeof modal.show === 'function') {
          e.preventDefault();
          modal.show(trigger);
        }
      });

      var stored = '';
      try {
        stored = localStorage.getItem(STORAGE_KEY) || '';
      } catch (e) {}
      if (stored && normalisePostcode(stored)) {
        input.value = stored;
        runCheck(root, 'stored');
      } else {
        input.value = '';
        resultEl.hidden = false;
        resultEl.classList.remove('earliest-delivery__result--error');
        resultEl.innerHTML = (root.dataset.prefix || 'Earliest delivery') + ' to <button type="button" class="earliest-delivery__postcode-trigger" data-earliest-delivery-enter>' + (root.dataset.placeholder || 'Enter your postcode') + '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="earliest-delivery__chevron" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg></button>';
        setFormVisible(formEl, true);
        var config = getConfig(root);
        attachPostcodeToggleListener(root, '[data-earliest-delivery-enter]', formEl, input, '', countdownEl, resultEl, config, 'initial_prompt');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
